/**
 * L'historique comparable — les appels (V1.6-15.3).
 *
 * Trois règles, et la troisième est la raison d'être de ce module :
 *
 * 1. **lecture seule.** Aucune mutation n'existe ici, et aucune n'y entrera :
 *    consulter un historique n'écrit rien ;
 * 2. **l'écran ne recalcule rien.** Les comptes, la taille d'échantillon, la
 *    ventilation libre/contrainte arrivent calculés. Refaire une division côté
 *    client donnerait un second chiffre, et le jour où les deux divergent
 *    personne ne saurait lequel croire ;
 * 3. **aucune inférence.** Il n'existe ni score, ni classement, ni
 *    « fournisseur préféré ». Ces types décrivent des faits passés ; ce qu'on
 *    en conclut appartient à la personne qui lit.
 */
import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "./client";
import type { Uuid } from "./types";

/** Cinq minutes : un historique de douze mois ne bouge pas pendant qu'on le lit. */
const HISTORY_STALE_MS = 5 * 60 * 1000;

/**
 * Sur quoi repose la comparaison.
 *
 * Il n'existe **pas** de niveau « même famille » : le catalogue ne porte
 * aucune relation de famille ni de variante, et en nommer une sur la foi
 * d'une équivalence affirmerait ce que la donnée ne dit pas.
 */
export type ComparisonLevel =
  | "SAME_PRODUCT"
  | "VERIFIED_EQUIVALENT_PRODUCT"
  | "SAME_CATEGORY";

export interface SupplierSelection {
  supplier_id: string;
  /** Le nom **courant**, pas celui porté à l'époque. */
  supplier_name: string;
  decisions: number;
  free_decisions: number;
  constrained_decisions: number;
}

export interface OutcomeCounts {
  supplier_confirmed: number;
  supplier_declined: number;
  supplier_changes_requested: number;
  receipt_complete: number;
  abandoned: number;
  supplier_return: number;
  pending: number;
}

export interface DecisionSummary {
  decision_ref: string;
  decision_group_ref: string;
  revision: number;
  decision_at: string | null;
  actor_role: string | null;
  decision_origin: string;
  labels: string[];
  supplier_ids: string[];
  supplier_names: string[];
  currencies: string[];
  recommendation_observed: boolean;
  constraint_flags: string[];
  constrained: boolean;
  execution_mode: string;
  outcomes: OutcomeCounts;
  outcome_pending: boolean;
  completeness: string;
  comparison_level: string;
  comparison_reasons: string[];
  rfq_id: string;
  project_id: string | null;
}

export interface ComparableDecisionHistory {
  period_start: string;
  period_end: string;
  comparison_level: string;
  /** Ce que le filtre contient. */
  sample_size: number;
  /** Ce que le résumé a réellement relu — les comptes portent sur ce nombre. */
  evaluated_sample_count: number;
  /** Ce que la page rend. */
  returned_count: number;
  truncated: boolean;
  complete_sample_size: number;
  partial_sample_size: number;
  free_sample_size: number;
  constrained_sample_size: number;
  pending_outcome_count: number;
  decisions: DecisionSummary[];
  suppliers: SupplierSelection[];
  outcomes: OutcomeCounts;
  currencies: string[];
  completeness: string;
  warnings: string[];
  limit: number;
  offset: number;
}

export interface ComparableDecisionParams {
  productId?: Uuid;
  categoryId?: Uuid;
  projectId?: Uuid;
  level?: ComparisonLevel;
  currentDecisionRef?: Uuid;
  periodDays?: number;
  limit?: number;
}

export const learningKeys = {
  all: ["learning"] as const,
  comparable: (params: ComparableDecisionParams) =>
    ["learning", "comparable-decisions", params] as const,
};

function search(params: ComparableDecisionParams): string {
  const query = new URLSearchParams();
  if (params.productId) query.set("product_id", params.productId);
  if (params.categoryId) query.set("category_id", params.categoryId);
  if (params.projectId) query.set("project_id", params.projectId);
  if (params.level) query.set("level", params.level);
  if (params.currentDecisionRef) query.set("current_decision_ref", params.currentDecisionRef);
  if (params.periodDays) query.set("period_days", String(params.periodDays));
  if (params.limit) query.set("limit", String(params.limit));
  return query.toString();
}

/**
 * Les décisions comparables à un contexte d'achat.
 *
 * `enabled` est faux sans clé de comparabilité : appeler sans produit ni
 * catégorie rendrait une erreur, et afficher « aucun historique » à la place
 * laisserait croire que l'historique est vide alors que rien n'a été demandé.
 */
export function useComparableDecisions(params: ComparableDecisionParams) {
  const ready = Boolean(params.productId ?? params.categoryId);
  return useQuery({
    queryKey: learningKeys.comparable(params),
    queryFn: () =>
      apiFetch<ComparableDecisionHistory>(`/learning/comparable-decisions?${search(params)}`),
    enabled: ready,
    staleTime: HISTORY_STALE_MS,
  });
}
